import { ReactComponent as AiContextIcon } from 'module/ui/assets/svgs/ai-context.svg';
import { Editor } from '@tiptap/core';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

export interface SectionMenuTriggerProps {
  editor: Editor;
  sectionPos: number;
  nodeData?: any;
  documentAgentId?: string;
  onSectionClick?: (nodeData: any) => void;
}

const TriggerContainer = styled.div``;

export const SectionMenuTrigger: React.FC<SectionMenuTriggerProps> = ({
  editor,
  sectionPos,
  nodeData,
  onSectionClick,
}) => {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Calculate fixed position based on section's position
    const updatePosition = () => {
      if (!editor.view || editor.isDestroyed) {
        return;
      }

      try {
        const sectionElement = editor.view.nodeDOM(sectionPos) as HTMLElement;

        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();

          setPosition({
            top: rect.top + 8,
            left: rect.right + 8, // 32px from right edge (24px button + 8px padding)
          });
        }
      } catch {
        // Editor view not ready yet
        return;
      }
    };

    updatePosition();

    // Update position on scroll or resize
    const handleUpdate = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    // Update on editor changes
    const observer = new MutationObserver(handleUpdate);

    if (editor.view?.dom?.parentElement) {
      observer.observe(editor.view.dom.parentElement, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      observer.disconnect();
    };
  }, [editor, sectionPos]);

  const handleClick = useCallback(() => {
    if (onSectionClick && nodeData) {
      onSectionClick(nodeData);
    }
  }, [onSectionClick, nodeData]);

  return (
    <TriggerContainer
      ref={containerRef}
      className="section-menu-trigger"
      style={{
        position: 'fixed',
        top: `${position?.top}px`,
        left: `${position?.left}px`,
        zIndex: 1000,
      }}
    >
      <AiContextIcon onClick={handleClick} size={24} />
    </TriggerContainer>
  );
};
